import moment from 'moment'

interface DateFormatSpec {
    // User-facing format label. May include tokens like `±HH:mm` that moment
    // itself does not understand; use `normalizeDateFormatForParsing` before
    // handing the string to moment for strict parsing.
    format: string
    regex: RegExp
    hasTime: boolean
    // Sibling format that shares the same regex shape and only differs in
    // day/month ordering. When both siblings match, `resolveDayMonthAmbiguity`
    // decides based on values whose first or second numeric part exceeds 12.
    ambiguousPair?: string
}

// Formats whose `format` string cannot be passed directly to moment because
// they contain `±HH:mm` (a display convention, not a moment token). The
// mapped format uses moment's `Z` token which accepts `Z` and `±HH[:]?mm`.
const PARSE_FORMAT_OVERRIDES: Record<string, string> = {
    'YYYY-MM-DDTHH:mm:ss±HH:mm': 'YYYY-MM-DDTHH:mm:ssZ',
    'YYYY-MM-DDTHH:mm:ss.SSS±HH:mm': 'YYYY-MM-DDTHH:mm:ss.SSSZ',
}

export function normalizeDateFormatForParsing(displayFormat: string): string {
    return PARSE_FORMAT_OVERRIDES[displayFormat] ?? displayFormat
}

const FORMAT_SPECS: DateFormatSpec[] = [
    // ── ISO 8601 with fractional seconds + timezone (most specific first) ──
    { format: 'YYYY-MM-DDTHH:mm:ss.SSSZ', regex: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/, hasTime: true },
    { format: 'YYYY-MM-DDTHH:mm:ss.SSS±HH:mm', regex: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}[+-]\d{2}:\d{2}$/, hasTime: true },
    { format: 'YYYY-MM-DDTHH:mm:ss.SSS', regex: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}$/, hasTime: true },
    { format: 'YYYY-MM-DDTHH:mm:ssZ', regex: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/, hasTime: true },
    { format: 'YYYY-MM-DDTHH:mm:ss±HH:mm', regex: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{2}:\d{2}$/, hasTime: true },
    { format: 'YYYY-MM-DDTHH:mm:ss', regex: /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/, hasTime: true },

    // ── 12-hour AM/PM with YYYY-first date ──
    { format: 'YYYY-MM-DD hh:mm:ss A', regex: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2} (?:AM|PM)$/i, hasTime: true },
    { format: 'YYYY-MM-DD hh:mm A', regex: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2} (?:AM|PM)$/i, hasTime: true },

    // ── 24-hour with YYYY-first date ──
    { format: 'YYYY-MM-DD HH:mm:ss', regex: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/, hasTime: true },
    { format: 'YYYY-MM-DD HH:mm', regex: /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/, hasTime: true },
    { format: 'YYYY/MM/DD HH:mm:ss', regex: /^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2}:\d{2}$/, hasTime: true },

    // ── Ambiguous DD/MM vs MM/DD with time ──
    { format: 'DD/MM/YYYY HH:mm:ss', regex: /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/, hasTime: true, ambiguousPair: 'MM/DD/YYYY HH:mm:ss' },
    { format: 'MM/DD/YYYY HH:mm:ss', regex: /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/, hasTime: true, ambiguousPair: 'DD/MM/YYYY HH:mm:ss' },
    { format: 'DD/MM/YYYY HH:mm', regex: /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/, hasTime: true, ambiguousPair: 'MM/DD/YYYY HH:mm' },
    { format: 'MM/DD/YYYY HH:mm', regex: /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/, hasTime: true, ambiguousPair: 'DD/MM/YYYY HH:mm' },
    { format: 'DD/MM/YYYY hh:mm A', regex: /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2} (?:AM|PM)$/i, hasTime: true, ambiguousPair: 'MM/DD/YYYY hh:mm A' },
    { format: 'MM/DD/YYYY hh:mm A', regex: /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2} (?:AM|PM)$/i, hasTime: true, ambiguousPair: 'DD/MM/YYYY hh:mm A' },

    // ── Unambiguous YYYY-first date-only ──
    { format: 'YYYY-MM-DD', regex: /^\d{4}-\d{2}-\d{2}$/, hasTime: false },
    { format: 'YYYY/MM/DD', regex: /^\d{4}\/\d{2}\/\d{2}$/, hasTime: false },
    { format: 'YYYY.MM.DD', regex: /^\d{4}\.\d{2}\.\d{2}$/, hasTime: false },
    { format: 'YYYYMMDD', regex: /^\d{8}$/, hasTime: false },

    // ── Ambiguous DD/MM vs MM/DD, both 2-digit and 1-digit variants ──
    { format: 'DD/MM/YYYY', regex: /^\d{2}\/\d{2}\/\d{4}$/, hasTime: false, ambiguousPair: 'MM/DD/YYYY' },
    { format: 'MM/DD/YYYY', regex: /^\d{2}\/\d{2}\/\d{4}$/, hasTime: false, ambiguousPair: 'DD/MM/YYYY' },
    { format: 'D/M/YYYY', regex: /^\d{1,2}\/\d{1,2}\/\d{4}$/, hasTime: false, ambiguousPair: 'M/D/YYYY' },
    { format: 'M/D/YYYY', regex: /^\d{1,2}\/\d{1,2}\/\d{4}$/, hasTime: false, ambiguousPair: 'D/M/YYYY' },
    { format: 'DD-MM-YYYY', regex: /^\d{2}-\d{2}-\d{4}$/, hasTime: false, ambiguousPair: 'MM-DD-YYYY' },
    { format: 'MM-DD-YYYY', regex: /^\d{2}-\d{2}-\d{4}$/, hasTime: false, ambiguousPair: 'DD-MM-YYYY' },
    { format: 'D-M-YYYY', regex: /^\d{1,2}-\d{1,2}-\d{4}$/, hasTime: false, ambiguousPair: 'M-D-YYYY' },
    { format: 'M-D-YYYY', regex: /^\d{1,2}-\d{1,2}-\d{4}$/, hasTime: false, ambiguousPair: 'D-M-YYYY' },
    { format: 'DD.MM.YYYY', regex: /^\d{2}\.\d{2}\.\d{4}$/, hasTime: false, ambiguousPair: 'MM.DD.YYYY' },
    { format: 'MM.DD.YYYY', regex: /^\d{2}\.\d{2}\.\d{4}$/, hasTime: false, ambiguousPair: 'DD.MM.YYYY' },
    { format: 'D.M.YYYY', regex: /^\d{1,2}\.\d{1,2}\.\d{4}$/, hasTime: false, ambiguousPair: 'M.D.YYYY' },
    { format: 'M.D.YYYY', regex: /^\d{1,2}\.\d{1,2}\.\d{4}$/, hasTime: false, ambiguousPair: 'D.M.YYYY' },

    // ── Month-name date formats (unambiguous) ──
    { format: 'DD MMM YYYY', regex: /^\d{2} [A-Za-z]{3} \d{4}$/, hasTime: false },
    { format: 'D MMM YYYY', regex: /^\d{1,2} [A-Za-z]{3} \d{4}$/, hasTime: false },
    { format: 'DD MMMM YYYY', regex: /^\d{2} [A-Za-z]{3,9} \d{4}$/, hasTime: false },
    { format: 'D MMMM YYYY', regex: /^\d{1,2} [A-Za-z]{3,9} \d{4}$/, hasTime: false },
    { format: 'MMM DD, YYYY', regex: /^[A-Za-z]{3} \d{2}, \d{4}$/, hasTime: false },
    { format: 'MMM D, YYYY', regex: /^[A-Za-z]{3} \d{1,2}, \d{4}$/, hasTime: false },
    { format: 'MMMM DD, YYYY', regex: /^[A-Za-z]{3,9} \d{2}, \d{4}$/, hasTime: false },
    { format: 'MMMM D, YYYY', regex: /^[A-Za-z]{3,9} \d{1,2}, \d{4}$/, hasTime: false },

    // ── Year-month only ──
    { format: 'YYYY-MM', regex: /^\d{4}-\d{2}$/, hasTime: false },
    { format: 'MM/YYYY', regex: /^\d{2}\/\d{4}$/, hasTime: false },
    { format: 'MMM YYYY', regex: /^[A-Za-z]{3} \d{4}$/, hasTime: false },
]

export interface DateDetection {
    format: string | null
    isDatetime: boolean
    isAmbiguous: boolean
}

interface CandidateEntry {
    spec: DateFormatSpec
    count: number
}

const MIN_MATCH_RATIO = 0.5

function tryParse(spec: DateFormatSpec, value: string): boolean {
    if (!spec.regex.test(value)) return false
    return moment(value, normalizeDateFormatForParsing(spec.format), true).isValid()
}

function collectCandidates(sampleValues: readonly string[]): { counts: Map<string, CandidateEntry>; matchedCount: number; totalCount: number } {
    const counts = new Map<string, CandidateEntry>()
    let matchedCount = 0
    let totalCount = 0

    for (const raw of sampleValues) {
        const v = raw.trim()
        if (!v) continue
        totalCount++
        let matched = false
        for (const spec of FORMAT_SPECS) {
            if (!tryParse(spec, v)) continue
            const existing = counts.get(spec.format)
            if (existing) existing.count++
            else counts.set(spec.format, { spec, count: 1 })
            matched = true
        }
        if (matched) matchedCount++
    }

    return { counts, matchedCount, totalCount }
}

function extractDateSeparator(format: string): '/' | '-' | '.' | null {
    for (const ch of format) {
        if (ch === '/' || ch === '-' || ch === '.') return ch
    }
    return null
}

function resolveDayMonthAmbiguity(sampleValues: readonly string[], separator: '/' | '-' | '.'): 'day-first' | 'month-first' | 'ambiguous' {
    const escapedSep = separator === '.' ? String.raw`\.` : separator
    const rex = new RegExp(String.raw`^(\d{1,2})` + escapedSep + String.raw`(\d{1,2})` + escapedSep + String.raw`\d{4}`)
    let dayFirst = 0
    let monthFirst = 0
    for (const raw of sampleValues) {
        const v = raw.trim()
        if (!v) continue
        const m = rex.exec(v)
        if (!m) continue
        const first = Number.parseInt(m[1], 10)
        const second = Number.parseInt(m[2], 10)
        if (Number.isFinite(first) && first > 12 && first <= 31) dayFirst++
        else if (Number.isFinite(second) && second > 12 && second <= 31) monthFirst++
    }
    if (dayFirst > 0 && monthFirst === 0) return 'day-first'
    if (monthFirst > 0 && dayFirst === 0) return 'month-first'
    return 'ambiguous'
}

function pickBetweenPair(topFormat: string, pairFormat: string, mode: 'day-first' | 'month-first'): string {
    const dayFirstFormat = topFormat.startsWith('D') ? topFormat : pairFormat
    const monthFirstFormat = topFormat.startsWith('M') ? topFormat : pairFormat
    return mode === 'day-first' ? dayFirstFormat : monthFirstFormat
}

function pickPreferredFormat(counts: Map<string, CandidateEntry>, sampleValues: readonly string[]): DateDetection {
    if (counts.size === 0) return { format: null, isDatetime: false, isAmbiguous: false }

    const ordered = [...counts.values()].sort((a, b) => b.count - a.count)
    const top = ordered[0]

    if (top.spec.ambiguousPair && counts.has(top.spec.ambiguousPair)) {
        const separator = extractDateSeparator(top.spec.format)
        if (!separator) {
            return { format: top.spec.format, isDatetime: top.spec.hasTime, isAmbiguous: true }
        }
        const resolution = resolveDayMonthAmbiguity(sampleValues, separator)
        if (resolution === 'ambiguous') {
            return { format: top.spec.format, isDatetime: top.spec.hasTime, isAmbiguous: true }
        }
        const chosen = pickBetweenPair(top.spec.format, top.spec.ambiguousPair, resolution)
        return { format: chosen, isDatetime: top.spec.hasTime, isAmbiguous: false }
    }

    return { format: top.spec.format, isDatetime: top.spec.hasTime, isAmbiguous: false }
}

export function detectDateFormat(sampleValues: readonly string[]): DateDetection {
    const { counts, matchedCount, totalCount } = collectCandidates(sampleValues)
    if (totalCount === 0 || matchedCount / totalCount < MIN_MATCH_RATIO) {
        return { format: null, isDatetime: false, isAmbiguous: false }
    }
    return pickPreferredFormat(counts, sampleValues)
}

// Public catalog of the display format strings this detector can produce.
// Consumers (e.g. the column-config dropdown) can render this list so users
// can override the detected format with any option the parser understands.
export const SUPPORTED_DATE_FORMATS: readonly string[] = FORMAT_SPECS.map((s) => s.format)
